import { datadogLogs } from '@datadog/browser-logs';
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePaymentsTranslation } from 'src/hooks/usePaymentsTranslation';
import { formatPhoneNumber } from 'src/util/formatPhoneNumber';
import classes from './PadAgreement.module.scss';

const PaymentsCaLink = () => (
  <a href="https://www.payments.ca" target="_blank" rel="noopener noreferrer">
    www.payments.ca
  </a>
);

const AgencyInfo = ({ data }: { data: PadAgreementData }) => (
  <div className={classes.agencyInfo}>
    <p>
      <strong>{data.name}</strong>
    </p>
    {data.addressLine1 && <p>{data.addressLine1}</p>}
    {data.addressLine2 && <p>{data.addressLine2}</p>}
    <p>
      {data.phone && formatPhoneNumber(data.phone)}
      {data.phone && data.email && ', '}
      {data.email}
    </p>
  </div>
);

export interface PadAgreementData {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  isRecurring?: boolean;
}

export const PadAgreement = () => {
  const { t } = usePaymentsTranslation();
  const [searchParams] = useSearchParams();
  // Get and parse the agency details from URL
  const padAgreementData = useMemo(() => {
    try {
      const padAgreementDataParam = searchParams.get('padAgreementData');
      return padAgreementDataParam ? (JSON.parse(decodeURIComponent(padAgreementDataParam)) as PadAgreementData) : null;
    } catch (error) {
      datadogLogs.logger.error('Failed to parse padAgreementData from URL', {
        error,
        padAgreementDataParam: searchParams.get('padAgreementData'),
      });
      return null;
    }
  }, [searchParams]);

  if (padAgreementData == null || padAgreementData.name == null) {
    return <p> {t('PAD_AGREEMENT_ERROR')}</p>;
  }
  if (padAgreementData.isRecurring) {
    return (
      <div className={classes.container}>
        <h1>{t('PAD_AGREEMENT_RECURRING_TITLE')}</h1>
        <p>
          {t('PAD_AGREEMENT_RECURRING_PARAGRAPH_1', undefined, {
            agencyName: padAgreementData.name || '',
          } as any)}
        </p>
        <p>{t('PAD_AGREEMENT_RECURRING_PARAGRAPH_2')}</p>
        <p>
          {padAgreementData.email
            ? t('PAD_AGREEMENT_RECURRING_PARAGRAPH_3_a', undefined, {
                agencyName: padAgreementData.name || '',
                agencyEmail: padAgreementData.email,
              } as any)
            : t('PAD_AGREEMENT_RECURRING_PARAGRAPH_3_a_NO_EMAIL', undefined, {
                agencyName: padAgreementData.name || '',
              } as any)}{' '}
          <PaymentsCaLink />
          {'. '}
          {t('PAD_AGREEMENT_RECURRING_PARAGRAPH_3_b', undefined, {
            agencyName: padAgreementData.name || '',
          } as any)}
        </p>
        <p>
          {t('PAD_AGREEMENT_RECURRING_PARAGRAPH_4')} <PaymentsCaLink />
        </p>
        <AgencyInfo data={padAgreementData} />
      </div>
    );
  }
  return (
    <div className={classes.container}>
      <h1>{t('PAD_AGREEMENT_ONETIME_TITLE')}</h1>
      <p>
        {t('PAD_AGREEMENT_ONETIME_PARAGRAPH_1', undefined, {
          agencyName: padAgreementData.name || '',
        } as any)}
      </p>
      <p>{t('PAD_AGREEMENT_ONETIME_PARAGRAPH_2')}</p>
      <p>{t('PAD_AGREEMENT_ONETIME_PARAGRAPH_3')}</p>
      <p>
        {t('PAD_AGREEMENT_ONETIME_PARAGRAPH_4')} <PaymentsCaLink />
      </p>
      <AgencyInfo data={padAgreementData} />
    </div>
  );
};
